/**
 * Zustand store for the broker bridge widgets (Broker Ticket / Broker Positions).
 *
 * Owns one shared SSE connection to the mt-ea service (ref-counted by widget
 * mounts), live quotes/positions/account state, and the tag→asset auto-switch
 * used by the headline feed integration:
 *
 * - mode "click": clicking a headline whose tags map to a tradable symbol
 *   switches the ticket's loaded asset.
 * - mode "feed": additionally, every newly arriving headline can switch it.
 * - Feed-driven switches are deferred while the user is mid-order-entry
 *   (see `formBusy`) so the asset never changes under a half-typed ticket.
 */
import { create } from "zustand";
import { devtools } from "zustand/middleware";
import toast from "react-hot-toast";
import { Tag } from "@/shared/api/newsquawk-headlines";
import {
  BrokerAccount,
  BrokerPosition,
  BrokerStreamEvent,
  TagMapEntry,
  TradeCommand,
  WorkingOrder,
  createOrder,
  getAccount,
  getOrder,
  getPositions,
  getSymbols,
  getWorkingOrders,
  streamUrl,
} from "@/shared/api/mt-ea";
import { newIdempotencyKey } from "@/shared/api/mt-ea/idempotency";

export type AutoSwitchMode = "off" | "click" | "feed";

/** Broadcast of a headline's tags; each widget instance decides whether to
 *  adopt it (see hooks/useBrokerWidgetSymbol). `seq` makes repeat signals for
 *  the same tags distinct so effects re-fire. */
export type TagSignal = {
  channel: "click" | "feed";
  tags: Tag[];
  seq: number;
};

type Quote = {
  bid: number;
  ask: number;
  /** broker minimum distance for pending orders, in price units */
  stop_dist?: number | null;
  /** broker price grid — orders must land on a multiple of this */
  tick_size?: number | null;
  digits?: number | null;
  ts: number;
};

const QUICK_TRADE_KEY = "nq:broker:quick-trade";

export type QuickTradePrefs = {
  /** Show Buy/Sell buttons directly on headlines whose tags map to a symbol. */
  enabled: boolean;
  /** Lot size those buttons submit with. */
  lots: number;
};

const loadQuickTrade = (): QuickTradePrefs => {
  try {
    const raw = localStorage.getItem(QUICK_TRADE_KEY);
    if (raw) return { enabled: false, lots: 0.1, ...JSON.parse(raw) };
  } catch {
    // corrupted prefs — fall through to defaults
  }
  return { enabled: false, lots: 0.1 };
};

type BrokerStore = {
  account: BrokerAccount | null;
  accountLoaded: boolean;
  positions: BrokerPosition[];
  workingOrders: WorkingOrder[];
  /** broker-native symbol → latest quote */
  quotes: Record<string, Quote>;
  /** canonical → broker-native symbol (null = not offered by this broker) */
  symbolMap: Record<string, string | null>;
  /** tag_id / lowercased tag name → canonical symbol (all tags) */
  tagMap: Record<string, string>;
  /** as tagMap but strong tags only (no bare currencies) — used by chips */
  tagMapStrong: Record<string, string>;
  /** last resolved command (fill/reject/expiry) for toast display */
  lastCommand: TradeCommand | null;

  /** Latest headline tag broadcast; widget instances opt in per their mode. */
  tagSignal: TagSignal | null;

  connect: () => void;
  disconnect: () => void;
  refresh: () => Promise<void>;
  /** Called by the headlines integration. Channel semantics in file header. */
  publishTags: (channel: "click" | "feed", tags: Tag[] | undefined) => void;
  /** First tag that maps to a symbol this broker trades, or null.
   *  strongOnly skips weak (bare-currency) tags — used by quick-trade chips. */
  symbolForTags: (tags: Tag[] | undefined, strongOnly?: boolean) => string | null;
  brokerSymbolFor: (canonical: string) => string | null;
  quoteFor: (canonical: string) => Quote | null;

  quickTrade: QuickTradePrefs;
  setQuickTrade: (prefs: Partial<QuickTradePrefs>) => void;
  /** One-click market order from a headline; resolves via toast. */
  quickOrder: (canonical: string, side: "market_buy" | "market_sell") => Promise<void>;
};

let eventSource: EventSource | null = null;
let subscriberCount = 0;
let reconnectTimer: ReturnType<typeof setTimeout> | null = null;

export const useBrokerStore = create<BrokerStore>()(
  devtools(
    (set, get) => ({
      account: null,
      accountLoaded: false,
      positions: [],
      workingOrders: [],
      quotes: {},
      symbolMap: {},
      tagMap: {},
      tagMapStrong: {},
      lastCommand: null,
      tagSignal: null,

      refresh: async () => {
        const account = await getAccount();
        set({ account, accountLoaded: true });
        if (!account) {
          set({ positions: [], workingOrders: [], symbolMap: {}, tagMap: {}, tagMapStrong: {} });
          return;
        }
        const [{ symbols, tag_map }, positions, workingOrders] = await Promise.all([
          getSymbols(),
          getPositions(),
          getWorkingOrders(),
        ]);
        const symbolMap: Record<string, string | null> = {};
        symbols.forEach((s) => (symbolMap[s.canonical] = s.broker_symbol));
        // Keyed by tag_id (primary — label spellings vary, IDs don't) and by
        // lowercased name as a fallback for rows without an ID.
        const tagMap: Record<string, string> = {};
        const tagMapStrong: Record<string, string> = {};
        tag_map.forEach((t: TagMapEntry) => {
          if (t.tag_id != null) tagMap[String(t.tag_id)] = t.canonical;
          tagMap[t.tag.toLowerCase()] = t.canonical;
          if (!t.weak) {
            if (t.tag_id != null) tagMapStrong[String(t.tag_id)] = t.canonical;
            tagMapStrong[t.tag.toLowerCase()] = t.canonical;
          }
        });
        set({ symbolMap, tagMap, tagMapStrong, positions, workingOrders });
      },

      connect: () => {
        subscriberCount++;
        if (eventSource) return;
        get()
          .refresh()
          .catch((error) => console.error("[broker] refresh failed:", error));

        const open = () => {
          if (subscriberCount === 0) return;
          eventSource = new EventSource(streamUrl());
          eventSource.onmessage = (message) => {
            const event: BrokerStreamEvent = JSON.parse(message.data);
            if (event.type === "quote") {
              set((state) => ({
                quotes: {
                  ...state.quotes,
                  [event.symbol]: {
                    bid: event.bid,
                    ask: event.ask,
                    ...(event as {
                      stop_dist?: number | null;
                      tick_size?: number | null;
                      digits?: number | null;
                    }),
                    ts: event.ts,
                  },
                },
              }));
            } else if (event.type === "positions") {
              set({ positions: event.positions });
            } else if (event.type === "working_orders") {
              set({ workingOrders: event.orders });
            } else if (event.type === "account_state") {
              set((state) =>
                state.account
                  ? { account: { ...state.account, state: event.state } }
                  : {}
              );
            } else if (event.type === "command") {
              set({ lastCommand: event.command });
            } else if (event.type === "status") {
              get().refresh().catch(() => undefined);
            }
          };
          eventSource.onerror = () => {
            // Token expiry / network blip: rebuild with a fresh token.
            eventSource?.close();
            eventSource = null;
            if (subscriberCount > 0 && !reconnectTimer) {
              reconnectTimer = setTimeout(() => {
                reconnectTimer = null;
                open();
              }, 3000);
            }
          };
        };
        open();
      },

      disconnect: () => {
        subscriberCount = Math.max(0, subscriberCount - 1);
        if (subscriberCount === 0) {
          eventSource?.close();
          eventSource = null;
          if (reconnectTimer) {
            clearTimeout(reconnectTimer);
            reconnectTimer = null;
          }
        }
      },

      symbolForTags: (tags, strongOnly = false) => {
        const { tagMap, tagMapStrong, symbolMap } = get();
        const map = strongOnly ? tagMapStrong : tagMap;
        if (!tags?.length) return null;
        const canonical = tags
          .map(
            (tag) =>
              map[String(tag.tag_id)] ??
              (tag.name ? map[tag.name.toLowerCase()] : undefined)
          )
          .find(Boolean);
        // Only offer symbols this broker actually trades.
        return canonical && symbolMap[canonical] ? canonical : null;
      },

      publishTags: (channel, tags) => {
        if (!tags?.length) return;
        // Broadcast only — each mounted widget instance applies its own mode
        // and form-busy rules (hooks/useBrokerWidgetSymbol).
        set((state) => ({
          tagSignal: { channel, tags, seq: (state.tagSignal?.seq ?? 0) + 1 },
        }));
      },

      brokerSymbolFor: (canonical) => get().symbolMap[canonical] ?? null,

      quoteFor: (canonical) => {
        const brokerSymbol = get().symbolMap[canonical];
        if (!brokerSymbol) return null;
        return get().quotes[brokerSymbol] ?? null;
      },

      quickTrade: loadQuickTrade(),

      setQuickTrade: (prefs) => {
        const next = { ...get().quickTrade, ...prefs };
        localStorage.setItem(QUICK_TRADE_KEY, JSON.stringify(next));
        set({ quickTrade: next });
      },

      quickOrder: async (canonical, side) => {
        const { quickTrade } = get();
        const label = `${side === "market_buy" ? "Buy" : "Sell"} ${quickTrade.lots} ${canonical}`;
        try {
          const command = await createOrder({
            idempotency_key: newIdempotencyKey(),
            type: side,
            symbol: canonical,
            lots: quickTrade.lots,
          });
          const toastId = toast.loading(`${label} — sending to terminal…`);
          // Poll for the result (quick orders can fire with no ticket widget
          // mounted, so we can't rely on the SSE command watcher being up).
          const deadline = Date.now() + 15_000;
          const poll = async () => {
            try {
              const latest = await getOrder(command.id);
              if (latest.status === "filled") {
                toast.success(`Filled: ${label} @ ${latest.result_price}`, { id: toastId });
                return;
              }
              if (latest.status === "rejected" || latest.status === "expired") {
                toast.error(
                  `${latest.status === "rejected" ? "Rejected" : "Expired"}: ${label} — ${latest.error_text || "no reason given"}`,
                  { id: toastId }
                );
                return;
              }
            } catch {
              // transient — keep polling until deadline
            }
            if (Date.now() < deadline) setTimeout(poll, 500);
            else toast.error(`No response for ${label} — check Broker Positions`, { id: toastId });
          };
          poll();
        } catch (error: any) {
          toast.error(error?.response?.data?.detail || `${label} failed to submit`);
        }
      },
    }),
    { name: "BrokerStore" }
  )
);
